import { useSeoBulkApply } from "@/contexts/SeoBulkApplyContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ChevronUp, ChevronDown, CheckCircle2, XCircle, ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function SeoBulkApplyIndicator() {
  const {
    status, total, done, currentTitle, results,
    minimized, setMinimized, recapOpen, openRecap, closeRecap, reset,
  } = useSeoBulkApply();
  const navigate = useNavigate();

  if (status === "idle") return null;

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const okCount = results.filter((r) => r.ok).length;
  const koCount = results.filter((r) => !r.ok).length;
  const running = status === "running";

  return (
    <>
      {/* Floating indicator (hidden while recap dialog is open) */}
      {!recapOpen && (
        <div
          className="fixed z-[60] bottom-4 right-4 w-[340px] max-w-[calc(100vw-2rem)] rounded-lg border bg-background shadow-lg overflow-hidden"
          role="status"
          aria-live="polite"
        >
          <button
            onClick={() => setMinimized(!minimized)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/40 hover:bg-muted/60 transition-colors"
          >
            <div className="flex items-center gap-2 min-w-0">
              {running ? (
                <Loader2 className="w-4 h-4 animate-spin text-sage shrink-0" />
              ) : (
                <ListChecks className="w-4 h-4 text-sage shrink-0" />
              )}
              <span className="text-sm font-medium truncate">
                {running ? "Application en cours" : "Application terminée"}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {done}/{total}
              </span>
            </div>
            {minimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {!minimized && (
            <div className="p-3 space-y-2">
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-sage transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {running && currentTitle && (
                <p className="text-xs text-muted-foreground truncate">
                  En cours : {currentTitle}
                </p>
              )}
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-sage">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {okCount} ok
                </span>
                {koCount > 0 && (
                  <span className="flex items-center gap-1 text-destructive">
                    <XCircle className="w-3.5 h-3.5" /> {koCount} échec{koCount > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {!running && (
                <div className="flex items-center justify-between pt-1">
                  <Button size="sm" variant="outline" onClick={openRecap}>
                    Voir le récap
                  </Button>
                  <Button size="sm" variant="ghost" onClick={reset}>
                    Fermer
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recap dialog */}
      <Dialog open={recapOpen} onOpenChange={(o) => (o ? openRecap() : closeRecap())}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Récapitulatif de l'application groupée</DialogTitle>
            <DialogDescription>
              {okCount} succès{koCount > 0 ? ` — ${koCount} échec${koCount > 1 ? "s" : ""}` : ""} sur {total} actions.
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-2 text-sm">
            {results.map((r) => (
              <li
                key={r.index}
                className={`rounded border p-3 ${r.ok ? "border-sage/30 bg-sage/5" : "border-destructive/40 bg-destructive/5"}`}
              >
                <div className="flex items-start gap-2">
                  {r.ok ? (
                    <CheckCircle2 className="w-4 h-4 text-sage mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium break-words">{r.title}</p>
                    {!r.ok && r.error && (
                      <p className="text-xs text-destructive mt-1 break-words whitespace-pre-wrap">
                        {r.error.status ? `[HTTP ${r.error.status}] ` : ""}
                        {r.error.message}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                closeRecap();
                navigate("/admin/seo");
              }}
            >
              Ouvrir le plan SEO
            </Button>
            <Button
              onClick={() => {
                closeRecap();
                reset();
              }}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
